import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { AuthService } from "src/app/features/auth/services/auth.service";
import {
  Gender,
  ImageResponse,
  ProductResponse,
} from "src/app/features/product-list/models/product-interface";
import { CommonDialogComponent } from "src/app/shared/components/common-dialog/common-dialog.component";
import { SnackbarComponent } from "src/app/shared/components/snackbar/snackbar.component";
import { CommonDialogData } from "src/app/shared/models/dialog.interface";
import { WishlistApiService } from "src/app/shared/services/wishlist-api.service";
import { AuthDialogService } from "src/app/shared/services/auth-dialog.service";
import { environment } from "src/environments/environment";
import { TranslateService } from "@ngx-translate/core";
import { getProductConfig } from "src/app/features/customize-board/models/product-type.config";
import { formatSoldQuantity, formatLikeQuantity } from "src/app/shared/utils/product-format.helper";

import { CartService } from "src/app/features/cart-detail/services/cart.service";
import { StockStatus } from "src/app/shared/constaints/common-contants";

@Component({
  selector: "app-home-popularproducts",
  templateUrl: "./home-popularproducts.component.html",
  styleUrls: ["./home-popularproducts.component.scss"],
  standalone: false,
})
export class HomePopularproductsComponent {
  @Input() popularProduct!: ProductResponse[];
  @Input() wishlistIds!: string[];
  @Input() titleKey = 'home.popular.title';
  @Input() showTitle = true;
  @Input() showViewAll = true;
  @Input() showCustomizationBadge = true;
  @Input() showMoqBadge = false;
  @Input() stackHeaderOnMobile = false;
  @Input() mobileGrid = false;
  @Input() loading = false;
  @Input() emptyMessageKey = '';
  @Input() viewAllQueryParams: Record<string, string | number | boolean> | null = null;
  @Output() wishlistChanged = new EventEmitter<void>();
  host_url: string = environment.imageURL;

  constructor(
    private router: Router,
    private wishlistService: WishlistApiService,
    private __snackBar: MatSnackBar,
    private authService: AuthService,
    private matDialog: MatDialog,
    private translate: TranslateService,
    private authDialogService: AuthDialogService,
    private cdr: ChangeDetectorRef,
    private cartService: CartService
  ) {}

  onQuickAddToCart(product: ProductResponse, event: Event): void {
    event.stopPropagation();
    if (this.isOutOfStock(product)) {
      return;
    }
    this.cartService.quickAddToCart(product);
  }

  /** True once product has no stock left. */
  isOutOfStock(item: ProductResponse): boolean {
    const p = item?.product;
    if (!p) return false;
    if (p.isOutOfStock === true || (p.quantityInStock !== undefined && p.quantityInStock !== null && p.quantityInStock <= 0) || p.stockStatus === StockStatus.OUTSTOCK) {
      return true;
    }
    if (item.productVariants && item.productVariants.length > 0) {
      let hasAnyStock = false;
      let hasSizes = false;
      for (const variant of item.productVariants) {
        if (variant.sizes && variant.sizes.length > 0) {
          hasSizes = true;
          if (variant.sizes.some((s: any) => (s.stockQuantity ?? 0) > 0)) {
            hasAnyStock = true;
            break;
          }
        }
      }
      if (hasSizes && !hasAnyStock) {
        return true;
      }
    }
    return false;
  }

  getMainProductImageUrl(prod: ProductResponse): string {
    const mainImage = prod.productImage?.find(
      (img: ImageResponse) => img.isMainImage
    );

    const image = mainImage ?? prod.productImage?.[0];
    const imageUrl = image?.imageThumbUrl?.trim() || image?.imageUrl?.trim();
    return imageUrl ? this.host_url + imageUrl : '';
  }

  brokenImageIds = new Set<string>();

  onImageError(productId: string): void {
    if (!this.brokenImageIds.has(productId)) {
      this.brokenImageIds.add(productId);
      this.cdr.markForCheck();
    }
  }

  getMinQuantity(item: ProductResponse): number {
    const qty = item?.product?.minQuantity;
    return typeof qty === 'number' && qty > 0 ? qty : 1;
  }

  formatSold(count?: number): string {
    return formatSoldQuantity(count, this.translate.currentLang);
  }

  formatLike(count?: number): string {
    return formatLikeQuantity(count, this.translate.currentLang);
  }

  /** Thêm hoặc xóa wishlist */
  toggleWishlist(productId: string, event: Event): void {
    event.stopPropagation();
    this.wishlistService.toggleWishlist(productId).subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  /** Kiểm tra sp có trong wishlist */
  isFavorite(productId: string): boolean {
    return this.wishlistService.isInWishlist(productId) || (this.wishlistIds?.includes(productId) ?? false);
  }

  onSelectProduct(prodId: string) {
    this.router.navigate(["/product", prodId]);
  }

  getProductPrice(item: any): number {
    if (!item?.product) return 0;
    if (item.productVariants && item.productVariants.length > 0) {
      for (const variant of item.productVariants) {
        if (variant.sizes && variant.sizes.length > 0) {
          const prices = variant.sizes
            .map((s: any) => {
              const dPrice = s.genderDetails?.[0]?.price;
              return Number(dPrice) > 0 ? Number(dPrice) : (Number(s.price) > 0 ? Number(s.price) : 0);
            })
            .filter((p: number) => p > 0);
          if (prices.length > 0) {
            return Math.min(...prices);
          }
        }
      }
    }
    return item.product.price ?? 0;
  }

  goToCustomize(item: any, event: Event) {
    event.stopPropagation();
    
    if (!item || !item.product || this.isOutOfStock(item)) return;

    const prodConfig = getProductConfig(item.product.productCategoryNameCode ?? '');
    const isCupOrCase = prodConfig.isCupLike;
    const shouldCheckSizes = !isCupOrCase || !!prodConfig.hasSizePicker;

    let sizePrice = item.product.price ?? 0;
    let colorId = '';
    let sizeName = '';
    let sizeGender: any = '';

    if (item.productVariants && item.productVariants.length > 0) {
      const firstVariant = item.productVariants[0];
      colorId = firstVariant.colorId;

      if (shouldCheckSizes && firstVariant.sizes && firstVariant.sizes.length > 0) {
        let selectedSize: any = null;

        for (const s of firstVariant.sizes) {
          if (s.genderDetails && s.genderDetails.length > 0) {
             const maleDetail = s.genderDetails.find((d: any) => d.gender === Gender.Male) || s.genderDetails[0];
             selectedSize = {
                ...s,
                gender: maleDetail.gender,
                price: Number(maleDetail.price) > 0 ? Number(maleDetail.price) : (Number(s.price) > 0 ? Number(s.price) : sizePrice)
             };
             break;
          }
        }

        if (!selectedSize) {
           selectedSize = firstVariant.sizes[0];
        }

        if (selectedSize) {
          sizeName = selectedSize.sizeName || '';
          sizeGender = selectedSize.gender ?? '';
          sizePrice = Number(selectedSize.price) > 0 ? Number(selectedSize.price) : sizePrice;
        }
      }
    }

    this.router.navigate(["/customize", item.product.id], {
        queryParams: {
            colorId: colorId,
            productId: item.product.id,
            sizePrice: sizePrice,
            sizeName: sizeName,
            sizeGender: sizeGender,
            from: 'home'
        },
        state: { fromUrl: '/' },
    });
  }
}
